import { FC } from 'react';
import { TableColumn, TableAction, TableSelection } from '../types';
import { formatValue } from '../utils';

interface TableBodyProps {
  data: any[];
  columns: TableColumn[];
  actions?: TableAction[];
  selectable?: boolean;
  selection?: TableSelection;
  onSelectionChange?: (selection: TableSelection) => void;
  striped?: boolean;
  hover?: boolean;
}

export const TableBody: FC<TableBodyProps> = ({
  data,
  columns,
  actions,
  selectable,
  selection,
  onSelectionChange,
  striped = true,
  hover = true,
}) => {
  const handleRowSelect = (rowIndex: number, checked: boolean) => {
    if (!onSelectionChange || !selection) return;

    const newSelectedRows = checked
      ? [...selection.selectedRows, rowIndex]
      : selection.selectedRows.filter(index => index !== rowIndex);

    onSelectionChange({
      selectedRows: newSelectedRows,
      selectAll: newSelectedRows.length === data.length,
    });
  };

  const isRowSelected = (rowIndex: number) => {
    return selection?.selectedRows.includes(rowIndex) || false;
  };

  const renderCellContent = (column: TableColumn, value: any, row: any) => {
    if (column.render) {
      return column.render(value, row);
    }

    if (column.formatter) {
      return column.formatter(value, row);
    }

    return formatValue(value, column.type);
  };

  const renderActions = (row: any, rowIndex: number) => {
    if (!actions || actions.length === 0) return null;

    const visibleActions = actions.filter(action =>
      action.visible ? action.visible(row) : true
    );

    if (visibleActions.length === 0) return null;

    // If 3 or fewer actions, show as icon buttons
    if (visibleActions.length <= 3) {
      return (
        <>
          {visibleActions.map((action, idx) => {
            const isDisabled = action.disabled ? action.disabled(row) : false;
            return (
              <button
                key={idx}
                className={`btn p-0 border-0 bg-transparent me-2 ${
                  action.icon && action.icon.includes('trash')
                    ? 'text-hover-danger'
                    : `${action.icon.includes('eye') ? '' : 'text-hover-primary'}`
                }`}
                onClick={() => action.action(row)}
                disabled={isDisabled}
                title={action.label}
                style={{ boxShadow: 'none' }}
              >
                <i className={`${action.icon} fs-4 cursor-pointer ${action.hoverColor ? action.hoverColor : 'text-hover-primary'}`}></i>
              </button>
            );
          })}
        </>
      );
    }

    // If more than 3 actions, show first 3 as icon buttons, rest in dropdown
    const firstThree = visibleActions.slice(0, 3);
    const rest = visibleActions.slice(3);

    return (
      <>
        {firstThree.map((action, idx) => {
          const isDisabled = action.disabled ? action.disabled(row) : false;
          return (
            <button
              key={idx}
              className="btn p-0 border-0 bg-transparent me-2"
              onClick={() => action.action(row)}
              disabled={isDisabled}
              title={action.label}
              style={{ boxShadow: 'none' }}
            >
              <i className={`${action.icon} fs-3 cursor-pointer ${action.hoverColor ? action.hoverColor : 'text-hover-primary'}`}></i>
            </button>
          );
        })}
        <div className="dropdown d-inline">
          <button
            className="btn btn-icon btn-sm btn-light"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fas fa-ellipsis-v"></i>
          </button>
          <ul className="dropdown-menu">
            {rest.map((action, actionIndex) => {
              const isDisabled = action.disabled ? action.disabled(row) : false;
              return (
                <li key={actionIndex}>
                  <button
                    className={`dropdown-item ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => !isDisabled && action.action(row)}
                    disabled={isDisabled}
                  >
                    <i className={`${action.icon} me-2`}></i>
                    {action.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </>
    );
  };

  if (data.length === 0) {
    return (
      <tbody>
        <tr>
          <td
            colSpan={columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)}
            className="text-center text-muted py-10"
          >
            <div className="d-flex flex-column align-items-center">
              <i className="fas fa-inbox fs-2x text-muted mb-3"></i>
              <span>Nenhum registro encontrado</span>
            </div>
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {data.map((row, rowIndex) => (
        <tr
          key={rowIndex}
          className={`
            ${striped && rowIndex % 2 === 1 ? 'table-striped' : ''}
            ${hover ? 'table-hover' : ''}
            ${isRowSelected(rowIndex) ? 'table-active' : ''}
          `}
        >
          {selectable && (
            <td>
              <div className="form-check form-check-sm form-check-custom form-check-solid">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isRowSelected(rowIndex)}
                  onChange={(e) => handleRowSelect(rowIndex, e.target.checked)}
                />
              </div>
            </td>
          )}

          {columns
            .filter(col => col.visible !== false)
            .map((column) => (
              <td
                key={column.value}
                className={`
                  ${column.align === 'center' ? 'text-center' : ''}
                  ${column.align === 'right' ? 'text-end' : ''}
                 
                  `}
              >
                {renderCellContent(column, row[column.value], row)}
              </td>
            ))}

          {actions && actions.length > 0 && (
            <td className="text-end">
              {renderActions(row, rowIndex)}
            </td>
          )}
        </tr>
      ))}
    </tbody>
  );
};
